import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Award, Truck, ShieldCheck, Heart } from "lucide-react";
import { useStore, inr, optimizedImg } from "../store";

export default function HomePage() {
  const { products } = useStore();
  const newArrivals = products.filter(p => p.collections?.includes("new-arrival")).slice(0, 6);
  const giftHer = products.filter(p => p.collections?.includes("gift-her")).slice(0, 4);
  const giftHim = products.filter(p => p.collections?.includes("gift-him")).slice(0, 4);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="grid md:grid-cols-2 min-h-[80vh]">
          <div className="flex items-center px-8 md:px-16 py-16" style={{ background: "var(--background)" }}>
            <div className="max-w-lg">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
                <div className="overline mb-6" style={{ color: "var(--secondary)" }}>Winter Collection 2026</div>
                <h1 className="font-serif text-6xl md:text-7xl font-light leading-[1.02] tracking-tighter" style={{ color: "var(--primary)" }}>
                  Heirlooms<br/><em className="font-light">for tomorrow.</em>
                </h1>
                <p className="mt-8 text-base leading-relaxed opacity-75 max-w-md">
                  Hand-crafted jewelry made for the modern soul. Made in Delhi, India. 7-day easy returns · Free shipping above ₹2000.
                </p>
                <div className="mt-10 flex gap-4 flex-wrap">
                  <Link to="/shop/women" data-testid="hero-shop-btn"><button className="btn-gold">Shop the Collection <ArrowRight size={16} className="inline ml-2"/></button></Link>
                  <Link to="/collection/wedding" data-testid="hero-bridal-btn"><button className="btn-outline">Bridal Edit</button></Link>
                </div>
              </motion.div>
            </div>
          </div>
          <motion.div initial={{ opacity: 0, scale: 1.02 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.2 }} className="relative min-h-[50vh] md:min-h-full">
            <img src={optimizedImg("https://images.pexels.com/photos/1162983/pexels-photo-1162983.jpeg", 1200)} alt="hero" className="w-full h-full object-cover" fetchpriority="high" decoding="async" width="1200" height="1500"/>
            <div className="absolute bottom-6 left-6 text-white/95 px-4 py-2 backdrop-blur-md" style={{ background: "rgba(11,40,31,0.4)" }}>
              <div className="overline">The Kanchan Edit</div>
              <div className="font-serif text-lg">Now Live →</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Marquee */}
      <section className="border-y overflow-hidden py-4" style={{ borderColor: "var(--border)", background: "var(--muted)" }}>
        <div className="marquee-track whitespace-nowrap">
          {[...Array(2)].map((_, k) => (
            <div key={k} className="flex gap-12 items-center px-6 text-sm">
              <span className="flex items-center gap-2"><Award size={14} style={{color:"var(--secondary)"}}/> Assured Quality Craftsmanship</span><span>•</span>
              <span className="flex items-center gap-2"><Truck size={14} style={{color:"var(--secondary)"}}/> Free Shipping above ₹2000</span><span>•</span>
              <span className="flex items-center gap-2"><ShieldCheck size={14} style={{color:"var(--secondary)"}}/> 7-Day Easy Returns</span><span>•</span>
              <span className="flex items-center gap-2"><Sparkles size={14} style={{color:"var(--secondary)"}}/> Made in Delhi, India</span><span>•</span>
            </div>
          ))}
        </div>
      </section>

      {/* CATEGORIES BENTO */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24" data-testid="categories-section">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="overline mb-3" style={{ color: "var(--secondary)" }}>Curated Categories</div>
            <h2 className="font-serif text-5xl md:text-6xl tracking-tighter" style={{ color: "var(--primary)" }}>Shop by silhouette</h2>
          </div>
          <Link to="/shop/women" className="text-sm underline underline-offset-4 hidden md:inline">View all →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
          {[
            { name: "Necklaces", to: "/shop/women/necklaces", img: "https://images.pexels.com/photos/15622999/pexels-photo-15622999.jpeg", big: true },
            { name: "Rings", to: "/shop/women/rings", img: "https://images.unsplash.com/photo-1605100804763-247f67b3557e" },
            { name: "Earrings", to: "/shop/women/earrings", img: "https://images.unsplash.com/photo-1758995116383-f51775896add" },
            { name: "Bangles", to: "/shop/women/bangles-bracelets", img: "https://images.unsplash.com/photo-1606293926249-ed22e446d476" },
          ].map((c, i) => (
            <Link to={c.to} key={c.name} data-testid={`category-${c.name.toLowerCase()}`}
                  className={`group product-img-wrap relative aspect-[4/5] ${c.big ? "md:col-span-2 md:row-span-2 md:aspect-auto md:h-full" : ""}`}>
              <img src={optimizedImg(c.img, 800)} alt={c.name} className="product-img absolute inset-0 w-full h-full object-cover" loading="lazy" decoding="async"/>
              <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(11,40,31,0.75) 100%)" }}/>
              <div className="absolute bottom-6 left-6 text-white">
                <div className="overline opacity-80">Explore</div>
                <div className="font-serif text-3xl mt-1">{c.name}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Bridal banner */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24">
        <div className="relative overflow-hidden aspect-[16/7] product-img-wrap grain">
          <img src={optimizedImg("https://images.pexels.com/photos/30276932/pexels-photo-30276932.jpeg", 1600)} className="product-img w-full h-full object-cover" alt="bridal" loading="lazy" decoding="async"/>
          <div className="absolute inset-0 flex flex-col justify-center items-start px-10 md:px-20" style={{ background: "linear-gradient(90deg, rgba(11,40,31,0.75) 0%, rgba(11,40,31,0.1) 60%)" }}>
            <div className="overline mb-3" style={{ color: "var(--secondary)" }}>The Wedding Story</div>
            <h3 className="font-serif text-4xl md:text-6xl text-white tracking-tighter max-w-xl leading-[1.05]">Every heirloom<br/>tells a wedding.</h3>
            <Link to="/collection/wedding" data-testid="bridal-cta"><button className="btn-gold mt-8">Discover Bridal</button></Link>
          </div>
        </div>
      </section>

      {/* NEW ARRIVALS */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24" data-testid="new-arrivals-section">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="overline mb-3" style={{ color: "var(--secondary)" }}>Just In</div>
            <h2 className="font-serif text-5xl tracking-tighter" style={{ color: "var(--primary)" }}>New Arrivals</h2>
          </div>
          <Link to="/collection/new-arrival" className="text-sm underline underline-offset-4 hidden md:inline">View all →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-8 md:gap-12">
          {newArrivals.map(p => (<ProductCard product={p} key={p.id}/>))}
        </div>
      </section>

      {/* GIFTS */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24" data-testid="gifts-section">
        <div className="text-center mb-14">
          <div className="overline mb-3" style={{ color: "var(--secondary)" }}>The Gift Edit</div>
          <h2 className="font-serif text-5xl tracking-tighter" style={{ color: "var(--primary)" }}>Made to be gifted.</h2>
          <p className="opacity-70 mt-3 text-sm">Curated pieces to surprise the special ones in your life.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 md:gap-10 mb-14">
          <Link to="/collection/gift-her" className="group relative aspect-[16/9] product-img-wrap" data-testid="gift-her-card">
            <img src="/images/gift-for-her.jpg" className="product-img absolute inset-0 w-full h-full object-cover" alt="Gift for her — elegant pendant necklace" loading="lazy" decoding="async"/>
            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(11,40,31,0.7) 100%)" }}/>
            <div className="absolute inset-0 flex flex-col justify-end p-8 md:p-10 text-white">
              <div className="overline mb-2" style={{color:"var(--secondary)"}}>For Her</div>
              <div className="font-serif text-4xl md:text-5xl tracking-tighter">Gift For Her</div>
              <div className="text-sm mt-3 opacity-80">Delicate pendants, timeless studs & more.</div>
              <div className="mt-5 flex items-center gap-2 text-sm font-medium">Shop the Edit <ArrowRight size={14}/></div>
            </div>
          </Link>
          <Link to="/collection/gift-him" className="group relative aspect-[16/9] product-img-wrap" data-testid="gift-him-card">
            <img src="/images/gift-for-him.jpg" className="product-img absolute inset-0 w-full h-full object-cover" alt="Gift for him — stylish men's accessory" loading="lazy" decoding="async"/>
            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(11,40,31,0.85) 100%)" }}/>
            <div className="absolute inset-0 flex flex-col justify-end p-8 md:p-10 text-white">
              <div className="overline mb-2" style={{color:"var(--secondary)"}}>For Him</div>
              <div className="font-serif text-4xl md:text-5xl tracking-tighter">Gift For Him</div>
              <div className="text-sm mt-3 opacity-80">Signet rings, chains, kadas & cufflinks.</div>
              <div className="mt-5 flex items-center gap-2 text-sm font-medium">Shop the Edit <ArrowRight size={14}/></div>
            </div>
          </Link>
        </div>

        {/* Gift picks */}
        <div className="grid md:grid-cols-2 gap-16">
          <div>
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-serif text-2xl tracking-tighter" style={{color:"var(--primary)"}}>For Her — Top Picks</h3>
              <Link to="/collection/gift-her" className="text-xs underline">View all</Link>
            </div>
            <div className="grid grid-cols-2 gap-6">{giftHer.slice(0,4).map(p => <ProductCard key={p.id} product={p} small/>)}</div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-serif text-2xl tracking-tighter" style={{color:"var(--primary)"}}>For Him — Top Picks</h3>
              <Link to="/collection/gift-him" className="text-xs underline">View all</Link>
            </div>
            <div className="grid grid-cols-2 gap-6">{giftHim.slice(0,4).map(p => <ProductCard key={p.id} product={p} small/>)}</div>
          </div>
        </div>
      </section>

      {/* Editorial testimonials */}
      <section className="py-24" style={{background:"var(--muted)"}} data-testid="testimonials-section">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <div className="overline mb-3" style={{color:"var(--secondary)"}}>Loved by 12,000+ women</div>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tighter" style={{color:"var(--primary)"}}>What our patrons say</h2>
          <div className="grid md:grid-cols-3 gap-8 mt-12">
            {[
              {name:"Priya S.", city:"Mumbai", text:"The Kundan haar was the star of my wedding. Every guest asked where it was from."},
              {name:"Ananya R.", city:"Delhi", text:"Ordered the diamond studs for my mom — she hasn't taken them off since."},
              {name:"Kavya M.", city:"Bengaluru", text:"Craftsmanship is beyond words. Also the packaging is *chef's kiss*."},
            ].map((t,i) => (
              <div key={i} className="p-6 text-left border" style={{background:"white", borderColor:"var(--border)"}}>
                <div className="text-sm opacity-80 leading-relaxed">"{t.text}"</div>
                <div className="mt-4 font-serif text-lg" style={{color:"var(--primary)"}}>{t.name}</div>
                <div className="text-xs opacity-60">{t.city}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export const ProductCard = React.memo(function ProductCard({ product, small }) {
  const { toggleWishlist, inWishlist } = useStore();
  const img1 = optimizedImg(product.images?.[0], small ? 500 : 800);
  const img2 = optimizedImg(product.images?.[1] || product.images?.[0], small ? 500 : 800);
  const saved = inWishlist(product.id);
  return (
    <div className="group relative" data-testid={`product-card-${product.slug}`}>
      <Link to={`/product/${product.slug}`} className="block">
        <div className="relative product-img-wrap aspect-[4/5] mb-4" style={{ background: "var(--muted)" }}>
          <img src={img1} alt={product.name} className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500 group-hover:opacity-0" loading="lazy" decoding="async" width="500" height="625"/>
          <img src={img2} alt="" className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700" loading="lazy" decoding="async" width="500" height="625"/>
        </div>
      </Link>
      <button onClick={(e) => { e.preventDefault(); toggleWishlist(product); }}
        className="absolute top-3 right-3 p-2 bg-white/90 hover:bg-white shadow-sm z-10"
        aria-label="wishlist" data-testid={`wishlist-btn-${product.slug}`}>
        <Heart size={14} fill={saved ? "var(--secondary)" : "none"} stroke={saved ? "var(--secondary)" : "currentColor"}/>
      </button>
      <div className="overline opacity-60 mb-1">{product.material}</div>
      <Link to={`/product/${product.slug}`}>
        <div className={`font-serif ${small ? "text-base" : "text-xl"} leading-tight`} style={{ color: "var(--primary)" }}>{product.name}</div>
      </Link>
      <div className="mt-1.5 flex items-baseline gap-2">
        <span className="font-medium text-sm">{inr(product.price)}</span>
        {product.original_price && (<span className="text-xs line-through opacity-50">{inr(product.original_price)}</span>)}
      </div>
    </div>
  );
});
